import React from "react";
import { Button, Pagination } from "@pd/pd-ui-kit";
import { format } from "date-fns";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  getEndOfDayWithTimestamp,
  getFullTimeWithTimestamp,
} from "~/libs/getDateOptions";
import {
  useGetEmailLogsListQuery,
  useGetEmailLogsReportLazyQuery,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import EmailLogsFilter from "./Filter";
import {
  getEmailLogsColumns,
  LOGS_EMAIL_SORTABLE_COLUMNS,
} from "./model/tableAdapter";
import { LOG_OPTIONS_EMAIL } from "./options";

import s from "./styles.less";

/** Строка id из URL ("1,2,3") в массив для API */
const parseIds = (raw: unknown): string[] | undefined => {
  if (!raw) return undefined;
  const list = String(raw)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  return list.length ? list : undefined;
};

const EmailLogs = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { notifyReport } = useNotify();
  const setData = useSetLocationData();
  const {
    page,
    count,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  // Ключи фильтров приходят из URL и не описаны в типе useGetLocationData
  const { dealerIds, userIds, name, fromDate, toDate } =
    useGetLocationData() as Record<string, unknown>;

  // Бэкенд LogGetter.getEmailLogs не принимает order/orderBy,
  // поэтому сортировка клиентская, а в URL хранится только её состояние
  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: LOGS_EMAIL_SORTABLE_COLUMNS,
    setData,
  });

  const tableSortField =
    sortField && LOGS_EMAIL_SORTABLE_COLUMNS.includes(sortField)
      ? sortField
      : undefined;

  const { handleCount, handlePage } = usePaginationParams();

  const filterVariables = {
    dealerIds: parseIds(dealerIds),
    userIds: parseIds(userIds),
    name: name ? String(name) : undefined,
    fromDate: fromDate ? getFullTimeWithTimestamp(String(fromDate)) : undefined,
    toDate: toDate ? getEndOfDayWithTimestamp(String(toDate)) : undefined,
  };

  const { data, loading } = useGetEmailLogsListQuery({
    variables: {
      page,
      count,
      ...filterVariables,
    },
    fetchPolicy: "no-cache",
  });

  const { total, items } = get(data, "LogGetter.getEmailLogs", {
    items: [],
    total: 0,
  });

  const [getEmailLogsReport] = useGetEmailLogsReportLazyQuery({
    onCompleted(reportData) {
      const hash = get(reportData, `LogGetter.getEmailLogsReport.fileHash`);
      hash && notifyReport(hash);
    },
    fetchPolicy: "no-cache",
  });

  const getReport = () => {
    // Для отчёта даты обязательны: без фильтра берём текущий день
    getEmailLogsReport({
      variables: {
        fromDate:
          filterVariables.fromDate ??
          getFullTimeWithTimestamp(format(new Date(), "yyyy-MM-dd")),
        toDate: filterVariables.toDate ?? getEndOfDayWithTimestamp(new Date()),
        dealerIds: filterVariables.dealerIds,
        userIds: filterVariables.userIds,
        name: filterVariables.name,
      },
    });
  };

  const list = items.map((el) => ({
    ...el,
    email: el.email?.replace(/,/g, ", "),
    createdAtRaw: el.createdAt ? new Date(el.createdAt).getTime() : 0,
    createdAt: el.createdAt
      ? format(new Date(el.createdAt), "dd.MM.yyyy HH:mm")
      : undefined,
    status: el.decidedToSend
      ? el.sent
        ? t("logs.statusSentSuccess")
        : t("logs.statusSendError")
      : t("logs.statusNotSent"),
  }));

  return (
    <Wrapper.Content
      title={t("logs.titleEmail")}
      txtBtn={t("ui.back")}
      goBackHandler={() => history.push("/logs/")}
      filterComponent={<EmailLogsFilter />}
      controlComponent={
        <Button onClick={getReport} size={"middle"}>
          {t("logs.makeReport")}
        </Button>
      }
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={getEmailLogsColumns(
          t,
          LOG_OPTIONS_EMAIL,
          tableSortField,
          sortType,
        )}
        dataSource={list}
        rowKey="id"
        loading={loading}
        sortableColumns={LOGS_EMAIL_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? sortType : undefined}
        onSortChange={onSortChange}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
    </Wrapper.Content>
  );
};

export default EmailLogs;
